import React, { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router";

import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import {
  DisplayDataOwner,
  DisplayInProject,
  DisplayOpenJobs,
  DisplaySewerObjectId,
} from "../components/DataComponents";
import {
  ActionsSection,
  ButtonsSection,
  SubSection,
  DataSection,
} from "../../../components/data-components/sections/DataSections";
import { DisplayDynamicProperties } from "../../../components/data-components/DisplayDynamicProperties";
import PageLayout from "../../_layout/PageLayout";
import { NodeMenuItemsModel } from "./NodeMenuItems";
import {
  ViewSewerObject,
  ViewSewerObjectFromAssignedProject,
  EditSewerObject,
  DeleteSewerObject,
  ManageDeletedData,
  EditSewerObjectFromAssignedProject,
  DeleteSewerObjectsFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import EditButton from "../../../components/buttons/edit-button/EditButton";
import { CurrentNodeStorageModel } from "../../../services/sewer-object/CurrentNodeStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import { api } from "../../../services/api/ApiService";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import GenericObjectIssues from "../../../components/generic-object-issues/GenericObjectIssues";
import { appSessionStorage } from "../../../services/storage/SessionStorageService";
import { CollapsibleServiceModel } from "../../../services/collapsible/CollapsibleService";
import { navigatePath } from "../../../helpers/StringHelpers";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import useRouteService from "../../../services/route-service/RouteService";

export default observer(function ViewNode() {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const [currentNodeStorage] = useState(() =>
    CurrentNodeStorageModel.create({
      nodeId: urlParamsService.getNodeId()!,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    },
    { api })
  );

  console.log(currentNodeStorage.isDeleteInProgress);

  const collapsibleService = CollapsibleServiceModel.create(
    { collapsedContentKey: "genericIssuesNodeIsCollapsed" },
    { appSessionStorage }
  );

  const menuItems = useMemo(() => {
    const nodeMenuItems = NodeMenuItemsModel.create(
      {
        nodeId: currentNodeStorage.nodeId,
        parentIdFromPath: currentNodeStorage.parentEntityFromPath.id,
        shouldDisableMenuItems: currentNodeStorage.isDeleteInProgress,
      },
      { api }
    );

    return {
      staticItems: nodeMenuItems.getStaticMenuItems(),
    };
  }, [
    currentNodeStorage.isDeleteInProgress,
    currentNodeStorage.nodeId,
    currentNodeStorage.parentEntityFromPath.id,
  ]);

  const canUserEditNode = useMemo(
    () =>
      permissionsService.userHasOneOfThePermissions([
        EditSewerObject,
        EditSewerObjectFromAssignedProject,
      ]),
    []
  );
  const handleEditClick = () => history.push(`${history.location.pathname}/edit`);

  const canUserDeleteNode = useMemo(
    () =>
      permissionsService.userHasOneOfThePermissions([
        DeleteSewerObject,
        DeleteSewerObjectsFromAssignedProject,
      ]) && currentNodeStorage.currentNode.canDelete,
    [currentNodeStorage.currentNode.canDelete]
  );
  const tooltipDeleteNode = useMemo(
    () =>
      currentNodeStorage.currentNode.canDelete ? undefined : "ViewNode.delete.linkedobject.tooltip",
    [currentNodeStorage.currentNode.canDelete]
  );

  const handleDeleteNode = async () => {
    try {
      await currentNodeStorage.delete();
      history.push(`${navigatePath(history.location.pathname, -2)}`);
      snackbarNotificationsService.showSuccess("ViewNode.delete.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.node.delete");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentNodeStorage.parentEntityFromPath.type,
        name: await currentNodeStorage.getParentEntityName(currentNodeStorage.currentNode.project.name)
        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentNodeStorage.currentNode.name
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentNodeStorage,
    currentNodeStorage.currentNode.name,
    currentNodeStorage.currentNode.project.name,
    currentNodeStorage.parentEntityFromPath.type,
    history.location.pathname
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewSewerObject, ViewSewerObjectFromAssignedProject]}
      titleKey="ViewNode.title"
      titleContext={{ nodeName: currentNodeStorage.currentNode.name }}
      id="view-node"
      menuItems={menuItems}
      isLoading={currentNodeStorage.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <EditButton disabled={!canUserEditNode} onClick={handleEditClick} />
          <DeleteButton
            disabled={!canUserDeleteNode}
            tooltipKey={tooltipDeleteNode}
            confirmationAction={handleDeleteNode}
            confirmationModalTitleKey="ViewNode.delete"
            confirmationModalSubtitleKey="ViewNode.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <GenericObjectIssues
        collapsibleService={collapsibleService}
        genericIssues={currentNodeStorage.currentNode.genericIssues}
      />
      <DataSection labelKey="general.node.details">
        <SubSection>
          <DisplaySewerObjectId
            id={currentNodeStorage.currentNode.name}
            labelKey="sewer-objects.DisplayNodeId.label"
          />
          <DisplayDataOwner dataOwner={currentNodeStorage.currentNode.dataOwner} />
        </SubSection>
        <SubSection>
          <DisplayInProject project={currentNodeStorage.currentNode.project} enableRedirect />
          <DisplayOpenJobs openJobs={currentNodeStorage.currentNode.openJobs} />
        </SubSection>
        <SubSection>
          <DisplayDateCreated date={currentNodeStorage.currentNode.created} />
          <DisplayCreatedBy createdBy={currentNodeStorage.currentNode.createdBy.name} />
        </SubSection>
      </DataSection>
      <DisplayDynamicProperties
        labelKey="general.characteristics"
        properties={currentNodeStorage.currentNode.getDynamicPropertiesForView()}
      />
    </PageLayout>
  );
});
